
import React, { useState, useEffect } from "react";
import { DashboardNavbar } from "@/components/dashboard/DashboardNavbar";
import { ServiceManager } from "@/components/dashboard/ServiceManager";
import { ServiceType } from "@/types/booking";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { ServicesListSkeleton } from "@/components/ui/skeleton-block";

const ServicesPage = () => {
  const [services, setServices] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    document.title = "Services | Figgy.link";
  }, []);

  const { user } = useAuth();
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const { data: { user: authUser } } = await supabase.auth.getUser();
        const userId = authUser?.id || user?.id;
        if (!userId) { setLoading(false); return; }

        const { data: business, error: bizErr } = await supabase
          .from('business_profiles')
          .select('id')
          .eq('user_id', userId)
          .maybeSingle();
        if (bizErr || !business) { setLoading(false); return; }

        const { data, error } = await supabase
          .from('business_services')
          .select('*')
          .eq('business_id', business.id)
          .order('name');
        if (error) throw error;
        const mapped: ServiceType[] = (data || []).map(s => ({
          id: s.id,
          name: s.name,
          description: s.description || "",
          duration: s.duration,
          price: Number(s.price),
          waitlistEnabled: Boolean(s.waitlist_enabled ?? true),
          bufferTime: s.buffer_time ?? 0,
        }));
        setServices(mapped);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load services');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);
  
  const handleServicesChange = async (updatedServices: ServiceType[]) => {
    try {
      setServices(updatedServices);
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const userId = authUser?.id || user?.id;
      if (!userId) return;
      const { data: business } = await supabase
        .from('business_profiles')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();
      if (!business) return;

      // Upsert current list (simple approach: delete missing, upsert rest)
      const upserts = updatedServices.map(s => ({
        id: s.id || undefined,
        business_id: business.id,
        name: s.name,
        description: s.description,
        duration: s.duration,
        price: s.price,
        waitlist_enabled: s.waitlistEnabled ?? true,
        buffer_time: s.bufferTime ?? 0,
      }));

      const { error: upsertErr } = await supabase
        .from('business_services')
        .upsert(upserts)
        .select('id');
      if (upsertErr) throw upsertErr;

      // Fetch current to find deletions
      const { data: current } = await supabase
        .from('business_services')
        .select('id')
        .eq('business_id', business.id);
      const keepIds = new Set(upserts.filter(u => u.id).map(u => u.id as string));
      const toDelete = (current || [])
        .map(r => r.id)
        .filter((id: string) => !keepIds.has(id));
      if (toDelete.length > 0) {
        await supabase.from('business_services').delete().in('id', toDelete);
      }

      toast.success("Services saved");
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || 'Failed to save services');
    }
  };

  return (
    <div className="min-h-screen content-wrapper">
      <DashboardNavbar />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="text-2xl font-semibold text-white on-dark-bg">Services</h1>
              <p className="text-gray-300">
                Customize your service lineup to flex on the competition
              </p>
            </div>
          </div>
          
          {loading ? <ServicesListSkeleton /> : (
            <div className="bg-black/40 backdrop-blur-xl p-6 rounded-xl border border-white/10">
              <ServiceManager
                services={services}
                onServicesChange={handleServicesChange}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServicesPage;
